import { motion } from 'framer-motion';
import type { AccountingKPIs } from '@/hooks/useAccounting';
import { theme } from '@/lib/theme';

interface Props {
  kpis: AccountingKPIs;
  loading: boolean;
}

function formatCOP(amount: number): string {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: 'easeOut' } },
};

interface KPICardProps {
  label: string;
  value: string;
  subLabel: string;
  subValue: string;
  icon: string;
  iconBg: string;
  iconColor: string;
  accent?: boolean;
  loading: boolean;
}

function KPICard({ label, value, subLabel, subValue, icon, iconBg, iconColor, accent, loading }: KPICardProps) {
  return (
    <motion.div
      variants={cardVariants}
      className="rounded-xl p-5 border relative overflow-hidden"
      style={{
        backgroundColor: accent ? 'rgba(52,211,153,0.06)' : theme.colors.bgSurface,
        borderColor: accent ? 'rgba(52,211,153,0.20)' : theme.colors.border,
      }}
    >
      {accent && (
        <div
          className="absolute inset-0 opacity-30"
          style={{
            background: 'radial-gradient(ellipse at top right, rgba(52,211,153,0.12) 0%, transparent 70%)',
          }}
        />
      )}

      <div className="relative z-10">
        <div className="flex items-start justify-between mb-4">
          <div
            className="w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0"
            style={{ backgroundColor: iconBg }}
          >
            <i className={`${icon} text-base`} style={{ color: iconColor }} />
          </div>
          {loading && (
            <div className="w-16 h-3 bg-white/8 rounded animate-pulse" />
          )}
        </div>

        {loading ? (
          <div className="space-y-2 animate-pulse">
            <div className="h-7 bg-white/10 rounded w-32" />
            <div className="h-3 bg-white/6 rounded w-24" />
          </div>
        ) : (
          <>
            <p
              className="text-2xl font-bold tracking-tight mb-1"
              style={{
                color: accent ? '#34d399' : theme.colors.textPrimary,
                fontFamily: theme.fonts.body,
              }}
            >
              {value}
            </p>
            <p className="text-xs" style={{ color: theme.colors.textMuted }}>
              {label}
            </p>
            <div
              className="mt-3 pt-3 flex items-center justify-between"
              style={{ borderTop: `1px solid ${accent ? 'rgba(52,211,153,0.12)' : theme.colors.border}` }}
            >
              <span className="text-xs" style={{ color: theme.colors.textDisabled }}>
                {subLabel}
              </span>
              <span
                className="text-xs font-semibold"
                style={{ color: accent ? '#34d399' : theme.colors.textSecondary }}
              >
                {subValue}
              </span>
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
}

export default function AccountingKPIs({ kpis, loading }: Props) {
  const advancePct = kpis.totalRevenue > 0
    ? Math.round((kpis.advanceRevenue / kpis.totalRevenue) * 100)
    : 0;
  const remainingPct = kpis.totalRevenue > 0
    ? Math.round((kpis.remainingRevenue / kpis.totalRevenue) * 100)
    : 0;

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
    >
      <KPICard
        label="Ingresos totales"
        value={formatCOP(kpis.totalRevenue)}
        subLabel="Transacciones"
        subValue={`${kpis.transactionCount} pagos`}
        icon="ri-money-dollar-circle-line"
        iconBg="rgba(52,211,153,0.12)"
        iconColor="#34d399"
        accent
        loading={loading}
      />
      <KPICard
        label="Ingresos por anticipos"
        value={formatCOP(kpis.advanceRevenue)}
        subLabel="Del total"
        subValue={`${advancePct}%`}
        icon="ri-arrow-down-circle-line"
        iconBg="rgba(217,217,217,0.08)"
        iconColor={theme.colors.primary}
        loading={loading}
      />
      <KPICard
        label="Ingresos restantes"
        value={formatCOP(kpis.remainingRevenue)}
        subLabel="Del total"
        subValue={`${remainingPct}%`}
        icon="ri-check-double-line"
        iconBg="rgba(217,217,217,0.08)"
        iconColor={theme.colors.primary}
        loading={loading}
      />
      <KPICard
        label="Anticipos registrados"
        value={`${kpis.advanceCount}`}
        subLabel="Servicios completados"
        subValue={`${kpis.remainingCount}`}
        icon="ri-receipt-line"
        iconBg="rgba(217,217,217,0.08)"
        iconColor={theme.colors.primary}
        loading={loading}
      />
    </motion.div>
  );
}
